import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewDriverVerification } from 'features/admin/services/adminApi';
import { getOrgDriverLicenceStatus } from 'features/drivers/services/driverApi';
import { formatVehicleType } from 'features/drivers/lib/driverLicenceStatus';
import { queryKeys } from 'core/query/queryKeys';
import { ROUTES } from 'core/router/routes';
import LicenceStatusGrid from 'shared/components/provider/LicenceStatusGrid';
import type { DriverLicenceRow } from 'types/api.generated';

export default function AdminDriverReview() {
  const queryClient = useQueryClient();
  const [orgId, setOrgId] = useState('');
  const [userId, setUserId] = useState('');
  const [lookupOrgId, setLookupOrgId] = useState('');
  const [lookupUserId, setLookupUserId] = useState('');

  const [reviewRow, setReviewRow] = useState<DriverLicenceRow | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const licenceQuery = useQuery({
    queryKey: queryKeys.drivers.orgLicenceStatus(lookupOrgId, lookupUserId),
    queryFn: () => getOrgDriverLicenceStatus(lookupUserId, lookupOrgId),
    enabled: Boolean(lookupOrgId && lookupUserId),
  });

  const load = () => {
    if (!orgId.trim() || !userId.trim()) return;
    setMessage(null);
    setReviewRow(null);
    setLookupOrgId(orgId.trim());
    setLookupUserId(userId.trim());
  };

  const submitReview = async (verificationStatus: 'verified' | 'rejected') => {
    const data = licenceQuery.data;
    if (!data || !reviewRow?.verificationId) return;
    if (verificationStatus === 'rejected' && !reason.trim()) {
      setMessage('Rejection reason is required.');
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await reviewDriverVerification(data.profileId, {
        verificationId: reviewRow.verificationId,
        verificationStatus,
        rejectionReason:
          verificationStatus === 'rejected' ? reason.trim() : undefined,
      });
      setMessage(
        `${formatVehicleType(reviewRow.vehicleType)} licence ${verificationStatus}.`,
      );
      setReviewRow(null);
      setReason('');
      await queryClient.invalidateQueries({
        queryKey: queryKeys.drivers.orgLicenceStatus(lookupOrgId, lookupUserId),
      });
    } catch {
      setMessage('Review failed — check org ID, user ID, and permissions.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-8 font-display space-y-6">
      <Link to={ROUTES.ADMIN.USERS} className="text-sm text-primary font-bold">
        ← Users
      </Link>
      <h1 className="text-2xl font-black">Driver licence review</h1>
      <p className="text-sm text-slate-600">
        Load a driver&apos;s per-type licence grid, then approve or reject pending
        submissions.
      </p>

      <div className="space-y-3">
        <input
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          placeholder="Organisation ID (cuid)"
          className="w-full rounded-xl border px-3 py-2 text-sm"
        />
        <input
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="Driver user ID (uuid)"
          className="w-full rounded-xl border px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={load}
          disabled={licenceQuery.isFetching}
          className="bg-primary text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-50"
        >
          {licenceQuery.isFetching ? 'Loading…' : 'Load licences'}
        </button>
      </div>

      {licenceQuery.isError && lookupUserId && (
        <p className="text-sm text-red-600">Could not load licence status.</p>
      )}

      {licenceQuery.data && (
        <>
          <p className="text-sm text-slate-600">
            Profile <span className="font-mono text-xs">{licenceQuery.data.profileId}</span>
          </p>
          <LicenceStatusGrid
            licences={licenceQuery.data.licences}
            variant="admin"
            onReview={(row) => {
              setReviewRow(row);
              setReason('');
              setMessage(null);
            }}
          />
        </>
      )}

      {reviewRow && (
        <div className="bg-white border rounded-2xl p-6 space-y-3">
          <h2 className="font-bold">
            Review {formatVehicleType(reviewRow.vehicleType)}
          </h2>
          {reviewRow.document && (
            <a
              href={reviewRow.document.documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-primary"
            >
              View submitted document
            </a>
          )}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Rejection reason (required when rejecting)"
            className="w-full rounded-xl border px-3 py-2 text-sm"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={loading}
              onClick={() => void submitReview('verified')}
              className="bg-emerald-600 text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => void submitReview('rejected')}
              className="bg-red-600 text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-50"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => setReviewRow(null)}
              className="border px-4 py-2 rounded-xl text-sm font-bold text-slate-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {message && <p className="text-sm">{message}</p>}
    </div>
  );
}
